<template>
    <div class="UserForm">
        <div class="flex row-left-center mb-2">
            <div class="flex-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('users.form.name.label') }}
                </WText>
                <WFieldError
                    :validator="$v.form.name"
                    :label="$t('users.form.name.label')"
                    #default="{ errorMessage, listeners }"
                >
                    <WInput
                        v-model="form.name"
                        :error="errorMessage"
                        :placeholder="$t('users.form.name.placeholder')"
                        :disabled="isLoading"
                        name="name"
                        type="text"
                        v-on="listeners"
                    />
                </WFieldError>
            </div>
            <div class="flex-1 ml-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('users.form.email.label') }}
                </WText>
                <WFieldError
                    :validator="$v.form.email"
                    :label="$t('users.form.email.label')"
                    #default="{ errorMessage, listeners }"
                >
                    <WInput
                        v-model="form.email"
                        :error="errorMessage"
                        :placeholder="$t('users.form.email.placeholder')"
                        :disabled="isLoading"
                        name="email"
                        type="email"
                        v-on="listeners"
                    />
                </WFieldError>
            </div>
        </div>

        <div class="flex row-left-center mb-2">
            <div class="flex-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('users.form.role.label') }}
                </WText>
                <WFieldError
                    :validator="$v.form.role"
                    :label="$t('users.form.role.label')"
                    #default="{ errorMessage, listeners }"
                >
                    <WSelect
                        v-model="form.role"
                        name="role"
                        :placeholder="$t('users.form.role.placeholder')"
                        :options="roles"
                        :disabled="roleDisabled || isLoading"
                        v-on="listeners"
                    />
                </WFieldError>
            </div>
        </div>

        <div class="flex row-left-center mb-2">
            <div class="flex-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('users.form.password.label') }}
                </WText>
                <WFieldError
                    :validator="$v.form.password"
                    :label="$t('users.form.password.label')"
                    #default="{ errorMessage, listeners }"
                >
                    <WInput
                        v-model="form.password"
                        :error="errorMessage"
                        :placeholder="$t('users.form.password.placeholder')"
                        :disabled="isLoading"
                        name="password"
                        type="password"
                        v-on="listeners"
                    />
                </WFieldError>
            </div>
            <div class="flex-1 ml-1">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('users.form.passwordConfirm.label') }}
                </WText>
                <WFieldError
                    :validator="$v.form.passwordConfirm"
                    :label="$t('users.form.passwordConfirm.label')"
                    #default="{ errorMessage, listeners }"
                >
                    <WInput
                        v-model="form.passwordConfirm"
                        :error="errorMessage"
                        :placeholder="$t('users.form.passwordConfirm.placeholder')"
                        :disabled="isLoading"
                        name="passwordConfirm"
                        type="password"
                        v-on="listeners"
                    />
                </WFieldError>
            </div>
        </div>

        <div class="flex row-right-center">
            <WButton
                v-if="$DEV"
                outline
                color="accent-blue"
                :disabled="isLoading"
                @click="handleMock"
            >
                {{ $t('users.form.mock') }}
            </WButton>
            <WButton
                outline
                color="accent-blue"
                :disabled="isLoading"
                class="ml-2"
                @click="$emit('onReset')"
            >
                {{ $t('users.form.reset') }}
            </WButton>
            <WButton
                color="primary"
                :loading="isLoading"
                :disabled="isLoading"
                class="ml-2"
                @click="handleSubmit"
            >
                {{ $t('users.form.submit') }}
            </WButton>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { validationMixin } from 'vuelidate'
import {
    Api, apiRequest, rethrowError, notifyHttpError,
} from '@/api'
import { ROLES } from '@/entities/users'
import {
    required, requiredUppercase, minLength, maxLength, sameAs,
} from '@/utils/validators'
import { capitalize } from '@/utils/format'

const NORMALIZE_ROLES = ROLES.map(key => ({ key, label: capitalize(key) }))

const MOCK = {
    name: 'Test',
    email: 'test@gmail.com',
    role: 'admin',
    password: 'Asdf1234',
    passwordConfirm: 'Asdf1234',
}

export default {
    name: 'UserForm',
    mixins: [validationMixin],
    props: {
        value: VueTypes.shape({
            name: VueTypes.string,
            email: VueTypes.string,
            role: VueTypes.string,
            password: VueTypes.string,
            passwordConfirm: VueTypes.string,
        }).def({}).loose,
        roleDisabled: VueTypes.bool.def(false),
    },
    data () {
        return {
            form: {
                id: undefined,
                name: '',
                email: '',
                role: '',
                password: '',
                passwordConfirm: '',
                ...(this.value || {}),
            },

            roles: NORMALIZE_ROLES,
            isLoading: false,
        }
    },
    validations () {
        const { id } = this.form

        return {
            form: {
                name: { required },
                email: { required },
                role: { required },
                password: {
                    ...(!id ? { required } : {}), minLength: minLength(8), maxLength: maxLength(20), requiredUppercase,
                },
                passwordConfirm: {
                    ...(!id ? { required } : {}), minLength: minLength(8), maxLength: maxLength(20), requiredUppercase, sameAs: sameAs('password'),
                },
            },
        }
    },
    methods: {
        handleMock () {
            Object.keys(MOCK).forEach((field) => {
                this[field] = MOCK[field]
            })
        },
        handleSubmit () {
            this.$v.$touch()

            if (!this.$v.$invalid) {
                if ((this.form || {}).id !== undefined) this.editUser()
                else this.createUser()
            }
        },
        async createUser () {
            this.isLoading = true
            const {
                name, email, role, password, passwordConfirm,
            } = this.form
            const params = {
                name, email, role, password, passwordConfirm,
            }
            apiRequest(Api().admin.usersCreate(params))
                .then(() => this.$emit('onSubmit', this.form))
                .catch((error) => {
                    this.$emit('onError', error)
                    rethrowError(notifyHttpError())(error)
                })
                .finally(() => (this.isLoading = false))
        },
        async editUser () {
            this.isLoading = true
            const {
                id, name, email, role, password, passwordConfirm,
            } = this.form
            const params = {
                name,
                email,
                role,
                ...(password ? { password } : {}),
                ...(passwordConfirm ? { passwordConfirm } : {}),
            }
            apiRequest(Api().admin.usersEdit({ id, params }))
                .then(() => this.$emit('onSubmit', this.form))
                .catch((error) => {
                    this.$emit('onError', error)
                    rethrowError(notifyHttpError())(error)
                })
                .finally(() => (this.isLoading = false))
        },

    },
}
</script>
